/**
 * Spawn System - handles unit spawning from buildings
 */

import type { World, System } from '@/core/ecs/World';
import { ComponentTypes } from '@/core/ecs/Component';
import type { PositionComponent } from '@/game/components/Position';
import type { SpawnerComponent } from '@/game/components/Spawner';
import type { FactionComponent } from '@/game/components/Faction';
import { type BuildingComponent, getBuildingEfficiency } from '@/game/components/Building';
import { createUnitEntity } from '@/game/entities/createUnit';
import { EventBus } from '@/core/events/EventBus';
import type { FactionManager } from '@/game/factions/FactionManager';
import { UNIT_STATS } from '@/game/factions/FactionData';
import type { MovementComponent } from '@/game/components/Movement';
import { setMovementTarget } from '@/game/systems/MovementSystem';
import { isWalkable } from '@/core/utils/Pathfinding';

/**
 * Find a valid spawn position around a building
 * Tries multiple angles and distances to find a walkable tile
 */
function findValidSpawnPosition(
  buildingX: number,
  buildingZ: number,
  preferredAngle: number,
  minDist: number = 1.5,
  maxDist: number = 3.5
): { x: number; z: number } | null {
  // Try 8 directions at increasing distances
  const directions = 8;

  for (let dist = minDist; dist <= maxDist; dist += 0.5) {
    // Start from preferred angle, then try other directions
    for (let i = 0; i < directions; i++) {
      const angle = preferredAngle + (i * Math.PI * 2) / directions;
      const testX = buildingX + Math.cos(angle) * dist;
      const testZ = buildingZ + Math.sin(angle) * dist;

      // Check if this tile is walkable
      const tileX = Math.floor(testX);
      const tileZ = Math.floor(testZ);

      if (isWalkable(tileX, tileZ)) {
        // Return center of the tile for better positioning
        return { x: tileX + 0.5, z: tileZ + 0.5 };
      }
    }
  }

  return null;
}

export class SpawnSystem implements System {
  private factionManager: FactionManager;

  constructor(factionManager: FactionManager) {
    this.factionManager = factionManager;
  }

  update(world: World, deltaTime: number): void {
    const spawners = world.query([
      ComponentTypes.Position,
      ComponentTypes.Spawner,
      ComponentTypes.Faction,
      ComponentTypes.Building,
    ]);

    for (const entity of spawners) {
      const position = world.getComponent<PositionComponent>(entity, ComponentTypes.Position)!;
      const spawner = world.getComponent<SpawnerComponent>(entity, ComponentTypes.Spawner)!;
      const faction = world.getComponent<FactionComponent>(entity, ComponentTypes.Faction)!;
      const building = world.getComponent<BuildingComponent>(entity, ComponentTypes.Building)!;

      // Skip if building not constructed
      if (!building.isConstructed) continue;

      // Check if reached spawn limit
      if (spawner.maxSpawns > 0 && spawner.spawned >= spawner.maxSpawns) continue;

      // Check if faction can afford unit
      const unitStats = UNIT_STATS[spawner.unitType];
      if (!unitStats) continue;

      if (this.factionManager.getGold(faction.factionId) < unitStats.cost) continue;

      // Update spawn timer (building level affects spawn speed)
      const efficiency = getBuildingEfficiency(building.level);
      spawner.spawnTimer += deltaTime * efficiency;

      if (spawner.spawnTimer >= spawner.spawnInterval) {
        spawner.spawnTimer = 0;
        spawner.spawned++;

        // Spend gold
        this.factionManager.spendGold(faction.factionId, unitStats.cost);

        // Find a valid spawn position (not blocked by buildings or obstacles)
        const preferredAngle = Math.random() * Math.PI * 2;
        const spawnPos = findValidSpawnPosition(position.x, position.z, preferredAngle);

        // Skip if no valid position found (building completely surrounded)
        if (!spawnPos) {
          // Refund gold and reset timer to try again later
          this.factionManager.addGold(faction.factionId, unitStats.cost);
          spawner.spawned--;
          spawner.spawnTimer = spawner.spawnInterval * 0.5; // Try again sooner
          continue;
        }

        const spawnX = spawnPos.x;
        const spawnZ = spawnPos.z;

        // Create the unit
        const unitEntity = createUnitEntity(world, spawner.unitType, faction.factionId, spawnX, position.y, spawnZ);

        // If rally point set, assign as initial target with pathfinding
        if (spawner.rallyX !== null && spawner.rallyZ !== null) {
          const movement = world.getComponent<MovementComponent>(unitEntity, ComponentTypes.Movement);
          if (movement) {
            setMovementTarget(movement, spawner.rallyX, spawner.rallyZ, false);
          }
        }

        EventBus.emit('unit:spawn', {
          entityId: unitEntity,
          factionId: faction.factionId,
          unitType: spawner.unitType,
        });
      }
    }
  }
}
